/*
 * Paid-social 20% off email capture.
 *
 * Shows only to visitors who LANDED with the configured paid-media UTMs, once per
 * browser. Captures the email into Braze and logs a custom event that a Braze
 * canvas uses as its entry, which is what sends the unique discount code.
 *
 * Deliberately does NOT read localStorage `utm_data` (set in theme-scripts--us):
 * that store is merged and never cleared, so a visitor who ever arrived from paid
 * social would keep those UTMs forever and re-qualify on later direct visits.
 */
(function () {
  'use strict';

  var CFG = window.PSEC_CONFIG;
  if (!CFG) return;

  var root = document.getElementById('psec-root');
  if (!root) return;

  var SHOWN_KEY = 'arccos:psec:shown';
  var DONE_KEY = 'arccos:psec:done';
  var CTX_KEY = 'arccos:psec:ctx';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* ---------- storage helpers (private mode / blocked cookies safe) ---------- */

  function read(store, key) {
    try { return store.getItem(key); } catch (e) { return null; }
  }
  function write(store, key, value) {
    try { store.setItem(key, value); } catch (e) { /* no-op */ }
  }

  function isSuppressed() {
    if (CFG.testMode) return false;
    if (read(localStorage, DONE_KEY)) return true;

    var shownAt = parseInt(read(localStorage, SHOWN_KEY) || '0', 10);
    if (!shownAt) return false;

    var maxAge = (CFG.suppressDays || 365) * 86400000;
    return Date.now() - shownAt < maxAge;
  }

  /* ---------- eligibility ---------- */

  function utmsFromUrl() {
    var p = new URLSearchParams(window.location.search);
    var source = (p.get('utm_source') || '').trim().toLowerCase();
    var medium = (p.get('utm_medium') || '').trim().toLowerCase();
    var campaign = (p.get('utm_campaign') || '').trim().toLowerCase();

    if (campaign.indexOf(String(CFG.matchCampaign || '').toLowerCase()) === -1) return null;

    /*
     * The Arccos URL Parameter Standard makes `paidsocial` a utm_MEDIUM value; utm_source
     * is the platform (facebook, tiktok). Ads tagged to that standard therefore carry
     * utm_medium=paidsocial, not utm_source=paidsocial. Accept either spelling so the
     * popup fires whichever convention the media team ships.
     */
    var want = String(CFG.matchPaidSocial || 'paidsocial').toLowerCase();
    if (medium !== want && source !== want) return null;

    return {
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term'),
      // Paid-social click ids. theme-scripts--us reads the same three params, and they
      // attribute more reliably than utm_content, which depends on ad naming hygiene.
      fbclid: p.get('fbclid'),
      meta_campaign_id: p.get('campaign_id'),
      meta_ad_id: p.get('ad_id'),
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString()
    };
  }

  // The landing hit owns eligibility; it is then carried for the rest of the visit
  // so a click-through before submitting does not lose the attribution.
  function resolveContext() {
    if (CFG.testMode) {
      return utmsFromUrl() || { utm_medium: CFG.matchPaidSocial, utm_campaign: CFG.matchCampaign, test_mode: true };
    }

    var fresh = utmsFromUrl();
    if (fresh) {
      write(sessionStorage, CTX_KEY, JSON.stringify(fresh));
      return fresh;
    }

    var stored = read(sessionStorage, CTX_KEY);
    if (!stored) return null;
    try { return JSON.parse(stored); } catch (e) { return null; }
  }

  /* ---------- Braze ---------- */

  /*
   * The Braze Web SDK is already on the storefront via the Braze Shopify app embed
   * (settings_data.json -> shopify://apps/braze/blocks/brazeWebSDK). That embed owns
   * initialize() with the store's own API key and sdkUrl, so we must NEVER initialize
   * a second instance here — we attach to the one that is already there.
   *
   * Two timing facts about that embed drive the code below:
   *   1. It loads behind a 2s setInterval poller, so window.braze does not exist at
   *      DOMContentLoaded. We poll for it.
   *   2. The stub it installs queues every call into window.brazeQueue and replays it
   *      once the real SDK arrives, so calls made before init are not lost.
   */

  var BRAZE_WAIT_MS = 15000;

  function waitForBraze() {
    return new Promise(function (resolve, reject) {
      var started = Date.now();

      (function poll() {
        if (window.braze && typeof window.braze.getUser === 'function') {
          resolve(window.braze);
          return;
        }
        if (Date.now() - started > BRAZE_WAIT_MS) {
          reject(new Error('Braze Web SDK did not load'));
          return;
        }
        setTimeout(poll, 250);
      })();
    });
  }

  // Mirror the app embed's own condition: when it returns early, it never called
  // openSession, and an anonymous visitor would otherwise have no session to attach
  // this signup to.
  function ensureSession(braze) {
    try {
      var settings = JSON.parse(localStorage.getItem('brazeAppSettings') || '{}');
      var customer = JSON.parse(localStorage.getItem('customerData') || 'null');
      var embedOff = settings.appEmbedEnabled !== 'true';
      var loginOnly = settings.userTrackingBehavior === 'on_login' && (!customer || !customer.id);
      if (embedOff || loginOnly) braze.openSession();
    } catch (e) { /* no-op */ }
  }

  // Braze queues network calls, so flush explicitly and cap the wait — the shopper
  // should never be left staring at a spinner because a beacon is slow.
  function flush(braze) {
    return new Promise(function (resolve) {
      var settled = false;
      var done = function () { if (!settled) { settled = true; resolve(); } };
      setTimeout(done, 2500);
      try { braze.requestImmediateDataFlush(done); } catch (e) { done(); }
    });
  }

  function submitToBraze(email, ctx) {
    return waitForBraze().then(function (braze) {
      ensureSession(braze);

      var user = braze.getUser();

      // Alias on the email so repeat captures from another device land on one profile.
      try { user.addAlias(email.toLowerCase(), 'email_capture'); } catch (e) { /* no-op */ }

      user.setEmail(email);
      user.setEmailNotificationSubscriptionType(braze.User.NotificationSubscriptionTypes.OPTED_IN);

      if (CFG.subscriptionGroupId) {
        try { user.addToSubscriptionGroup(CFG.subscriptionGroupId); } catch (e) { /* no-op */ }
      }

      var props = {};
      Object.keys(ctx || {}).forEach(function (k) {
        if (ctx[k] !== null && ctx[k] !== undefined && ctx[k] !== '') props[k] = ctx[k];
      });
      props.offer = CFG.offerName;
      props.signup_source = 'popup';

      // Matches the CSS breakpoint, so this records which variant they actually saw.
      props.device = window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';

      // Joins this signup to GA4 / Snowplow. theme-scripts--us defines getGaClientId()
      // and caches a fallback in localStorage; use whichever is available.
      var gaId = null;
      try {
        gaId = (typeof window.getGaClientId === 'function' && window.getGaClientId())
          || localStorage.getItem('ga_client_id');
      } catch (e) { /* no-op */ }
      if (gaId) props.ga_client_id = gaId;

      braze.logCustomEvent(CFG.eventName, props);

      // Same hand-off the Braze embed uses for its own in-app-message captures, so
      // Shopify associates this email with the session (abandoned cart, etc.).
      try {
        if (window.Shopify && Shopify.analytics && Shopify.analytics.visitor) {
          Shopify.analytics.visitor({ email: email });
        }
      } catch (e) { /* no-op */ }

      return flush(braze);
    });
  }

  /* ---------- UI ---------- */

  var form = root.querySelector('.psec__form');
  var input = root.querySelector('.psec__input');
  var submit = root.querySelector('.psec__submit');
  var errorEl = root.querySelector('.psec__error');
  var successEmail = root.querySelector('.psec__success-email');
  var lastFocus = null;

  function open() {
    lastFocus = document.activeElement;
    root.setAttribute('data-open', 'true');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    write(localStorage, SHOWN_KEY, String(Date.now()));
    setTimeout(function () { if (input) input.focus(); }, 320);
    push('psec_popup_shown');
  }

  function close() {
    root.setAttribute('data-open', 'false');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function push(event, extra) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: event, offer: CFG.offerName };
    if (extra) Object.keys(extra).forEach(function (k) { payload[k] = extra[k]; });
    window.dataLayer.push(payload);
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.setAttribute('data-visible', 'true');
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.removeAttribute('data-visible');
    if (input) input.removeAttribute('aria-invalid');
  }

  function setBusy(busy) {
    submit.disabled = busy;
    if (busy) submit.setAttribute('data-busy', 'true');
    else submit.removeAttribute('data-busy');
  }

  root.querySelectorAll('[data-psec-close]').forEach(function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      close();
      push('psec_popup_dismissed');
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.getAttribute('data-open') === 'true') close();
  });

  // Keep tab focus inside the dialog while it is open.
  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var items = root.querySelectorAll('button, input, a[href]');
    var visible = Array.prototype.filter.call(items, function (el) { return el.offsetParent !== null; });
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  if (input) input.addEventListener('input', clearError);

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    clearError();

    var email = (input.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      showError('Please enter a valid email address.');
      input.focus();
      return;
    }

    setBusy(true);

    submitToBraze(email, resolveContext() || {})
      .then(function () {
        write(localStorage, DONE_KEY, String(Date.now()));
        if (successEmail) successEmail.textContent = email;
        root.setAttribute('data-state', 'success');
        push('psec_email_captured');
      })
      .catch(function (err) {
        setBusy(false);
        showError("Sorry, we couldn't sign you up just then. Please try again.");
        push('psec_error', { message: String((err && err.message) || err) });
      });
  });

  /* ---------- boot ---------- */

  function boot() {
    if (window.Shopify && window.Shopify.designMode) return;
    if (window.location.pathname === '/challenge') return;
    if (isSuppressed()) return;
    if (!resolveContext()) return;
    open();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
